import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { PaginatedResult } from '../../model/paginated-result';
import { IntegrationTemplate } from '../../models/integration-template.model';

export interface ToggleProposeResponse {
  id: string;
  can_propose_integrations: boolean;
}

export interface ListPendingParams {
  page: number;
  perPage: number;
}

@Injectable({ providedIn: 'root' })
export class IntegrationTemplateAdminService {

  private readonly adminBase = `${environment.apiUrl}/admin`;

  /**
   * Contador de plataformas pendentes de aprovação — fonte única de verdade
   * pro badge do menu e pra tela de Aprovações. Atualizado via
   * refreshPendingApprovals() após cada ação que muda o status (approve /
   * reject / save de novo template / delete).
   */
  private pendingApprovalsSubject = new BehaviorSubject<number>(0);
  pendingApprovals$: Observable<number> = this.pendingApprovalsSubject.asObservable();

  constructor(private http: HttpClient) {}

  /** Re-busca o total de pendentes e empurra no subject. Falhas zeram o contador. */
  refreshPendingApprovals(): void {
    this.listPending({ page: 1, perPage: 1 }).subscribe({
      next: (result) => this.pendingApprovalsSubject.next(result.total),
      error: () => this.pendingApprovalsSubject.next(0),
    });
  }

  clone(sourceId: string, newName: string): Observable<IntegrationTemplate> {
    return this.http.post<IntegrationTemplate>(
      `${this.adminBase}/integration-templates/${sourceId}/clone`,
      { new_name: newName },
    );
  }

  togglePropose(userId: string, canPropose: boolean): Observable<ToggleProposeResponse> {
    return this.http.patch<ToggleProposeResponse>(
      `${this.adminBase}/users/${userId}/toggle-propose-integrations`,
      { can_propose: canPropose },
    );
  }

  listPending(params: ListPendingParams): Observable<PaginatedResult<IntegrationTemplate[]>> {
    const httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('per_page', String(params.perPage));

    return this.http.get<PaginatedResult<IntegrationTemplate[]>>(
      `${this.adminBase}/integration-templates/pending`,
      { params: httpParams },
    );
  }

  approve(id: string): Observable<IntegrationTemplate> {
    return this.http.post<IntegrationTemplate>(
      `${this.adminBase}/integration-templates/${id}/approve`,
      {},
    );
  }

  reject(id: string, rejectionReason: string): Observable<IntegrationTemplate> {
    return this.http.post<IntegrationTemplate>(
      `${this.adminBase}/integration-templates/${id}/reject`,
      { rejection_reason: rejectionReason },
    );
  }
}
